import { UserIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchHistories } from "../../../api/histories";
import { TWclassNames } from "../../utils/Div";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { ResourceLoading } from "../../utils/ResourceLoading";
import {
  historyActions,
  historyActionTypes,
} from "./components";
import {
  isBatchHistoryDataShape,
  parseHistoryChanges,
} from "./historyPayloadUtils";
import { dateToString, productPath } from "../../Utils";
import { PMDialog } from "../../utils/PMDialog";

function historyVerbLabel(action, t) {
  const key = `histories.action.${action}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return historyActions[action] || action;
}

function outboundCommunicationId(item) {
  if (item?.outbound_communication_id) return item.outbound_communication_id;
  const data = item?.data && typeof item.data === "object" ? item.data : null;
  return data?.outbound_communication_id || null;
}

function outboundHistoryLabel(action, t) {
  if (action === "not_approved_notification") {
    return t("histories.outbound.not_approved");
  }
  if (action === "approved_notification") {
    return t("histories.outbound.approved");
  }
  return null;
}

const EDIT_RELATED_ACTIONS = new Set([
  "update",
  "status_update",
  "process",
  "update_after_process",
  "link_work_order",
  "unlink_work_order",
  "sync_from_work_order",
  "update_product",
  "send_notification",
  "send_notification_scheduled",
  "send_notification_failed",
  "new_agreement",
  "destroy",
]);

const parseGenericHistoryData = (dataString) => {
  try {
    const parsed =
      typeof dataString === "string" ? JSON.parse(dataString) : dataString;

    const length = parsed.updated_at?.length || 0;
    const entries = [];

    for (let i = 0; i < length; i++) {
      const changes = {};

      for (const key in parsed) {
        if (key !== "updated_at" && Array.isArray(parsed[key])) {
          let value = parsed[key][i];

          if (typeof value === "string") {
            const lower = value.toLowerCase();
            if (lower === "true" || lower === "1") {
              value = true;
            } else if (lower === "false" || lower === "0") {
              value = false;
            }
          } else if (typeof value === "number") {
            value = value === 1 ? true : value === 0 ? false : value;
          }

          changes[key] = value;
        }
      }

      entries.push({
        updatedAt: parsed.updated_at[i],
        changes,
      });
    }

    return entries;
  } catch (err) {
    console.error("Error parsing data string:", err);
    return [];
  }
};

const actorName = (item, t) =>
  item?.created_by?.name ??
  item?.created_by?.displayTitle ??
  t("histories.system_user");

/** Deviation events are mirrored to project/customer timelines; chip shows which list the row indexes. */
function deviationHistoryScopeChip(item, t) {
  if (item?.controller !== "deviation" || !item.module) return null;
  const key = `histories.deviation_scope.${item.module}`;
  const label = t(key);
  if (!label || label === key) return null;
  return (
    <span className="mt-1 inline-flex max-w-full rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-600">
      {label}
    </span>
  );
}

const ts = (iso) => {
  const n = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(n) ? n : 0;
};

const tripletexValidationMessage = (historyItem) => {
  const data =
    historyItem?.data && typeof historyItem.data === "object"
      ? historyItem.data
      : null;
  const messages = Array.isArray(data?.tripletex_validation_messages)
    ? data.tripletex_validation_messages
        .map((entry) => entry?.message)
        .filter(Boolean)
    : [];
  if (messages.length > 0) return messages.join(" | ");

  return data?.tripletex_message || data?.tripletex_developer_message || null;
};

function mapHistoryRows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    let parsedData = row?.data ? parseGenericHistoryData(row.data) : [];
    const dataObj =
      row?.data && typeof row.data === "object" && !Array.isArray(row.data)
        ? row.data
        : null;

    if (
      parsedData.length === 0 &&
      dataObj &&
      Object.keys(dataObj).length > 0 &&
      !isBatchHistoryDataShape(dataObj)
    ) {
      parsedData = [{ updatedAt: row.updated_at, changes: dataObj }];
    }

    const parsedChanges = parseHistoryChanges(row.changes);

    return {
      ...row,
      type: historyActionTypes[row.action],
      parsedData,
      parsedChanges,
    };
  });
}

function deriveLastUpdatedFromHistories(list) {
  if (!list.length) return null;
  let best = list[0];
  for (const h of list) {
    if (ts(h.updated_at) > ts(best.updated_at)) best = h;
  }
  return {
    at: best.updated_at,
    by: best.created_by,
  };
}

function deriveLastEditedFromHistories(list) {
  const candidates = list.filter((h) => EDIT_RELATED_ACTIONS.has(h.action));
  if (!candidates.length) return null;
  let best = candidates[0];
  for (const h of candidates) {
    if (ts(h.created_at) > ts(best.created_at)) best = h;
  }
  return {
    at: best.created_at,
    by: best.created_by,
  };
}

export const HistoryModuleView = (props) => {
  const { t } = useTranslation();
  const {
    variant = "embedded",
    open = true,
    entityMeta,
  } = props;
  const isPreview = variant === "preview";

  const [histories, setHistories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [actionFilter, setActionFilter] = useState("all");

  const per_page = isPreview ? 5 : 15;

  const baseFilters = useMemo(
    () => [
      {
        name: props.module,
        value: props.module_param,
      },
    ],
    [props.module, props.module_param]
  );

  const fetchControllerBase = useMemo(
    () => ({
      filters: baseFilters,
      per_page,
      query: "",
      sort: "desc",
      sortBy: "updated_at",
      view: "all",
    }),
    [baseFilters, per_page]
  );

  const loadHistories = useCallback(
    async (pageNum, append) => {
      if (!props.module || !props.module_param) {
        setHistories([]);
        setHasMore(false);
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      if (append) setIsLoadingMore(true);
      else setIsLoading(true);

      try {
        const res = await fetchHistories({
          ...fetchControllerBase,
          page: pageNum,
        });

        const raw = res?.data?.data;
        const batch = mapHistoryRows(raw);

        setHistories((prev) => {
          if (!append) return batch;
          const seen = new Set(prev.map((r) => r.id));
          const merged = [...prev];
          for (const row of batch) {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              merged.push(row);
            }
          }
          return merged;
        });

        setHasMore(batch.length >= per_page);
        setPage(pageNum);
      } catch (err) {
        console.error("Error fetching histories:", err);
        if (!append) setHistories([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [fetchControllerBase, props.module, props.module_param]
  );

  useEffect(() => {
    if (variant === "modal" && !open) return undefined;
    setActionFilter("all");
    loadHistories(1, false);
    return undefined;
  }, [variant, open, loadHistories]);

  function labelAndLink(item) {
    switch (item.controller) {
      case "order":
        return (
          <>
            {item?.order?.id ? (
              <Link
                className="cursor-pointer font-medium text-slate-900 hover:text-slate-700"
                to={
                  item.order?.id ? "/dashboard/orders/" + item.order?.id : null
                }
              >
                {t("Order") +
                  " " +
                  (item?.order?.order_number
                    ? item?.order?.order_number
                    : "(Slettet)")}
              </Link>
            ) : (
              <span>
                {t("Order") +
                  " " +
                  (item?.order?.order_number
                    ? item?.order?.order_number
                    : "(Slettet)")}
              </span>
            )}
          </>
        );
      case "report":
        return (
          <Link
            className="cursor-pointer font-medium text-slate-900 hover:text-slate-700"
            to={"/dashboard/service/reports/" + item.report?.id}
          >
            {t("Report") + " " + item?.report?.report_number}
          </Link>
        );
      case "customer_system":
        return "Customer System";
      case "text_message":
        if (item?.action === "new_message") {
          return "til " + item?.param_two;
        } else {
          return null;
        }
      case "product":
        return (
          <Link
            className="cursor-pointer font-medium text-slate-900 hover:text-slate-700"
            to={productPath({ product: item?.product })}
          >
            {t("Product")}
          </Link>
        );
      case "tripletex_api":
        return "Tripletex API";
      case "hour":
        return item?.module_param ? (
          <Link
            className="cursor-pointer font-medium text-slate-900 hover:text-slate-700"
            to={`/dashboard/hours/${item.module_param}`}
          >
            {t("Hour")} #{item.module_param}
          </Link>
        ) : (
          t("Hour")
        );
      case "deviation":
        if (item?.param != null && item.param !== "") {
          return (
            <Link
              className="cursor-pointer font-medium text-slate-900 hover:text-slate-700"
              to={`/dashboard/hse-ks/deviations/${item.param}`}
            >
              {t("Deviation")}
            </Link>
          );
        }
        return t("Deviation");
      case "meeting_minutes_meeting":
      case "meeting_minutes":
        return t("meeting_minutes");
      default:
        return item.controller;
    }
  }

  const historyContent = (item) => {
    if (item.action === "service_appointment_reminder") {
      return [t(`serviceReminders.${item.param_one}`), t(`serviceReminders.${item.param_two}`), t(`serviceReminders.state.${item.param_three}`)].join(" · ");
    }
    if (item.action === "sync_error" && typeof item.param_two === "string") {
      return item.param_two;
    }
    if (item.action === "tripletex_retry") {
      return t("histories.tripletex_retry_pending");
    }
    switch (item.controller) {
      case "order":
        return null;
      case "report":
        if (item.action === "process" || item.action === "update_after_process") {
          const content = [];
          if (item?.param_one) {
            content.push(item.param_one);
          }

          if (item?.parsedData?.length > 0) {
            const latestData = item.parsedData[item.parsedData.length - 1];
            const changes = latestData?.changes || {};

            if (changes.approved === true) {
              content.push("Status: Godkjent");
            } else if (changes.not_approved === true) {
              content.push("Status: Ikke godkjent");
            } else if (changes.updated_after_process === true) {
              content.push("Status: Oppdatert etter behandling");
            }
          }

          return content.length > 0 ? content.join(" | ") : null;
        }
        return null;
      case "customer_system":
        return null;
      case "text_message":
        return item?.param_one;
      case "tripletex_api":
        if (item.action === "sync_error") {
          return "Feilkode: " + item?.param_one;
        } else {
          return null;
        }
      case "customer":
        if (item.action === "lock" || item.action === "unlock") {
          const data = item?.data && typeof item.data === "object" ? item.data : {};
          return data.reason || data.note || data.previous_reason || null;
        }
        return null;
      case "hour":
        if (item.action?.startsWith?.("tripletex_sync")) {
          const validationMessage = tripletexValidationMessage(item);
          if (validationMessage) return validationMessage;

          const fromChanges = item?.parsedChanges?.reason;
          const fromData =
            item?.data && typeof item.data === "object"
              ? item.data.reason
              : null;
          const fromLegacy =
            item?.parsedChanges?._legacy &&
            typeof item.parsedChanges._legacy === "string"
              ? item.parsedChanges._legacy
              : null;
          const reason = fromChanges || fromData;
          if (reason) return String(reason);
          if (fromLegacy) return fromLegacy;
          return null;
        }
        return null;

      default:
        return null;
    }
  };

  const [selectedHistory, setSelectedHistory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openDialog = (item) => {
    setSelectedHistory(item);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setSelectedHistory(null);
    setDialogOpen(false);
  };

  const metaLines = useMemo(() => {
    const fromEntity = (at, byName) => {
      if (!at) return null;
      return {
        at,
        label: dateToString(at),
        by: byName || t("histories.unknown_user"),
      };
    };

    const updatedFromEntity = fromEntity(
      entityMeta?.updatedAt,
      entityMeta?.updatedByName
    );
    const editedFromEntity = fromEntity(
      entityMeta?.lastEditedAt ?? entityMeta?.updatedAt,
      entityMeta?.lastEditedByName ?? entityMeta?.updatedByName
    );

    const du = deriveLastUpdatedFromHistories(histories);
    const de = deriveLastEditedFromHistories(histories);

    const updated =
      updatedFromEntity ||
      (du
        ? {
            at: du.at,
            label: dateToString(du.at),
            by: actorName({ created_by: du.by }, t),
          }
        : null);

    const edited =
      editedFromEntity ||
      (de
        ? {
            at: de.at,
            label: dateToString(de.at),
            by: actorName({ created_by: de.by }, t),
          }
        : null);

    return { updated, edited };
  }, [histories, entityMeta, t]);

  const uniqueActions = useMemo(() => {
    const set = new Set(histories.map((h) => h.action).filter(Boolean));
    return Array.from(set).sort();
  }, [histories]);

  const filteredHistories = useMemo(() => {
    if (actionFilter === "all") return histories;
    return histories.filter((h) => h.action === actionFilter);
  }, [histories, actionFilter]);

  const actionLabel = (action) => historyVerbLabel(action, t);

  const handleLoadMore = () => {
    if (!hasMore || isLoadingMore) return;
    loadHistories(page + 1, true);
  };

  const shellClass =
    variant === "modal" || isPreview
      ? ""
      : "rounded-3xl border border-slate-200 bg-white p-5 shadow-sm";

  const sectionClass =
    variant === "modal" ? "space-y-4" : TWclassNames(shellClass, "space-y-4");

  return (
    <section
      aria-labelledby={isPreview ? undefined : "timeline-title"}
      className={sectionClass}
    >
      {isPreview ? null : (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <h2
          id="timeline-title"
          className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
        >
          {t("histories.title")}
        </h2>
        <dl className="mt-3 space-y-3 text-sm">
          <div>
            <dt className="font-medium text-slate-700">
              {t("histories.last_updated")}
            </dt>
            <dd className="mt-0.5 text-slate-600">
              {metaLines.updated ? (
                <>
                  <span className="font-medium text-slate-900">
                    {metaLines.updated.label}
                  </span>
                  <span className="text-slate-500">
                    {" "}
                    · {t("histories.by_prefix")} {metaLines.updated.by}
                  </span>
                </>
              ) : (
                <span className="text-slate-400">{t("histories.no_data")}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-700">
              {t("histories.last_edited")}
            </dt>
            <dd className="mt-0.5 text-slate-600">
              {metaLines.edited ? (
                <>
                  <span className="font-medium text-slate-900">
                    {metaLines.edited.label}
                  </span>
                  <span className="text-slate-500">
                    {" "}
                    · {t("histories.by_prefix")} {metaLines.edited.by}
                  </span>
                </>
              ) : (
                <span className="text-slate-400">{t("histories.no_data")}</span>
              )}
            </dd>
          </div>
        </dl>
      </div>
      )}

      {!isPreview && uniqueActions.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            {t("histories.filter_action")}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActionFilter("all")}
              className={TWclassNames(
                "rounded-full border px-3 py-1 text-xs font-medium transition",
                actionFilter === "all"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              )}
            >
              {t("histories.filter_all")}
            </button>
            {uniqueActions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => setActionFilter(action)}
                className={TWclassNames(
                  "rounded-full border px-3 py-1 text-xs font-medium capitalize transition",
                  actionFilter === action
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                )}
              >
                {actionLabel(action)}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        isPreview ? (
          <p className="py-4 text-sm text-[#7a8798]">{t("Loading...")}</p>
        ) : (
          <ResourceLoading />
        )
      ) : histories.length === 0 ? (
        isPreview ? (
          <p className="py-4 text-sm text-[#7a8798]">{t("project.overview.activity.empty")}</p>
        ) : (
          <ResourceEmpty />
        )
      ) : filteredHistories.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
          {t("histories.empty_filter")}
        </p>
      ) : (
        <ul role="list" className={isPreview ? "" : "space-y-3"}>
          {filteredHistories.map((item) => {
            const Icon = item.type?.icon || UserIcon;
            const iconWrap = item.type?.bgColorClass || "bg-slate-200";
            const extraContent = historyContent(item);
            const outboundId = outboundCommunicationId(item);
            const outboundLabel = outboundHistoryLabel(item.action, t);
            const verbText = outboundLabel || historyVerbLabel(item.action, t);
            const hasParsedChanges =
              item?.parsedChanges &&
              Object.keys(item.parsedChanges).length > 0;
            const showChangesBtn =
              (item?.parsedData?.length > 0) || hasParsedChanges;
            return (
              <li
                key={item.id}
                className={
                  isPreview
                    ? "border-b border-[#f2f5f9] bg-transparent py-3 shadow-none last:border-b-0"
                    : "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                }
              >
                <div className="flex gap-3">
                  <div
                    className={TWclassNames(
                      "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white",
                      iconWrap
                    )}
                  >
                    <Icon className="h-5 w-5 text-white" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                      <p className="text-sm text-slate-600">
                        <span className="font-medium text-slate-900">
                          {actorName(item, t)}
                        </span>{" "}
                        {outboundId ? (
                          <Link
                            className="font-medium text-[#1463C7] hover:underline"
                            to={`/dashboard/communications/${outboundId}`}
                          >
                            {verbText}
                          </Link>
                        ) : (
                          <span className="text-slate-500">{verbText}</span>
                        )}{" "}
                        <span className="font-medium text-slate-900">
                          {labelAndLink(item)}
                        </span>
                      </p>
                      <time
                        className="whitespace-nowrap text-xs text-slate-500 sm:text-right"
                        dateTime={item.created_at}
                      >
                        {dateToString(item.created_at)}
                      </time>
                    </div>
                    {extraContent ? (
                      <p className="mt-1 text-xs text-slate-600">{extraContent}</p>
                    ) : null}
                    {deviationHistoryScopeChip(item, t)}
                    {item?.param_one && item.action !== "process" && item.action !== "service_appointment_reminder" ? (
                      <p className="mt-1 text-xs italic text-slate-500">
                        {item.param_one}
                      </p>
                    ) : null}
                    {showChangesBtn ? (
                      <button
                        type="button"
                        className="mt-2 text-xs font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
                        onClick={() => openDialog(item)}
                      >
                        {t("Changes")}
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!isPreview && hasMore && !isLoading && histories.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoadingMore ? t("histories.loading_more") : t("histories.load_more")}
          </button>
        </div>
      )}

      {selectedHistory && (
        <PMDialog title={t("Changes")} open={dialogOpen} onClose={closeDialog}>
          {selectedHistory?.parsedChanges &&
            Object.keys(selectedHistory.parsedChanges).length > 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("histories.sync_changes_heading")}
                </p>
                {selectedHistory.parsedChanges._legacy != null &&
                String(selectedHistory.parsedChanges._legacy).trim().length >
                  0 ? (
                  <pre className="mb-3 whitespace-pre-wrap break-words font-mono text-xs text-slate-800">
                    {String(selectedHistory.parsedChanges._legacy)}
                  </pre>
                ) : null}
                {Object.entries(selectedHistory.parsedChanges).filter(
                  ([k]) => k !== "_legacy"
                ).length > 0 ? (
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {Object.entries(selectedHistory.parsedChanges)
                      .filter(([k]) => k !== "_legacy")
                      .map(([key, value]) => {
                        const formattedKey = key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase());
                        let formattedValue = value;
                        if (typeof value === "boolean") {
                          formattedValue = value ? t("Yes") : t("No");
                        } else if (typeof value === "object" && value !== null) {
                          formattedValue = JSON.stringify(value, null, 2);
                        }
                        return (
                          <li key={key}>
                            <strong>{formattedKey}:</strong>{" "}
                            <span className="whitespace-pre-wrap">
                              {formattedValue}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                ) : null}
              </div>
            )}

          {selectedHistory?.action === "process" && selectedHistory?.param_one && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="mb-1 text-sm font-semibold text-amber-900">
                {t("Process Note")}:
              </p>
              <p className="text-sm text-amber-800">{selectedHistory.param_one}</p>
            </div>
          )}

          {selectedHistory?.action === "update_after_process" &&
            selectedHistory?.param_one && (
              <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
                <p className="mb-1 text-sm font-semibold text-blue-900">
                  {t("Information")}:
                </p>
                <p className="text-sm text-blue-800">{selectedHistory.param_one}</p>
              </div>
            )}

          {selectedHistory?.parsedData?.length > 0 ? (
            <div className="flex flex-col gap-4">
              {selectedHistory.parsedData.map((entry, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-slate-200 bg-slate-50/80 p-4"
                >
                  {entry.updatedAt ? (
                    <div className="mb-2 text-xs text-slate-500">
                      {new Date(entry.updatedAt).toLocaleString()}
                    </div>
                  ) : null}
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {Object.entries(entry.changes || {}).map(([key, value]) => {
                      const formattedKey = key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase());

                      let formattedValue = value;
                      if (typeof value === "boolean") {
                        formattedValue = value ? t("Yes") : t("No");
                      } else if (key === "approved" && value === true) {
                        formattedValue = t("Godkjent");
                      } else if (key === "not_approved" && value === true) {
                        formattedValue = t("Ikke godkjent");
                      } else if (key === "updated_after_process" && value === true) {
                        formattedValue = t("Oppdatert etter behandling");
                      }

                      return (
                        <li key={key}>
                          <strong>{formattedKey}:</strong>{" "}
                          {typeof formattedValue === "string" &&
                          formattedValue.includes("<") ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: formattedValue,
                              }}
                            />
                          ) : (
                            <span>{formattedValue}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ) : selectedHistory?.parsedChanges &&
            Object.keys(selectedHistory.parsedChanges).length > 0 ? null : (
            <p>{t("No changes found")}</p>
          )}
        </PMDialog>
      )}
    </section>
  );
};
